import { useCallback, useEffect, useRef, useState } from "react";
//#region src/hooks/useSpeechRecognition.ts
/** Resolve the (possibly vendor-prefixed) constructor, or null when unavailable. */
const getSpeechRecognitionCtor = () => {
	if (typeof window === "undefined") return null;
	const w = window;
	return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};
/**
* Thin React wrapper over the browser-native `SpeechRecognition`. No backend,
* no API key — Chrome/Edge/Safari stream audio to the platform speech service
* and return text. Emits only FINAL chunks (interim results are requested for
* responsiveness but not surfaced, to avoid flickering partial text into the
* input). Feature-detected: `supported` is false where the API is missing
* (e.g. Firefox), so callers can hide the affordance.
*/
const useSpeechRecognition = ({ lang, onResult, onError }) => {
	const [supported, setSupported] = useState(false);
	const [listening, setListening] = useState(false);
	const recognitionRef = useRef(null);
	const onResultRef = useRef(onResult);
	const onErrorRef = useRef(onError);
	onResultRef.current = onResult;
	onErrorRef.current = onError;
	useEffect(() => {
		setSupported(getSpeechRecognitionCtor() !== null);
	}, []);
	const stop = useCallback(() => {
		recognitionRef.current?.stop();
	}, []);
	const start = useCallback(() => {
		if (recognitionRef.current) return;
		const Ctor = getSpeechRecognitionCtor();
		if (!Ctor) return;
		const recognition = new Ctor();
		recognition.lang = lang ?? (typeof navigator !== "undefined" ? navigator.language : "en-US") ?? "en-US";
		recognition.continuous = true;
		recognition.interimResults = true;
		recognition.onresult = (event) => {
			let finalChunk = "";
			for (let i = event.resultIndex; i < event.results.length; i += 1) {
				const result = event.results[i];
				if (result.isFinal) finalChunk += result[0].transcript;
			}
			if (finalChunk.trim()) onResultRef.current(finalChunk);
		};
		recognition.onerror = (event) => {
			onErrorRef.current?.(event.error ?? "speech-recognition-error");
		};
		recognition.onend = () => {
			recognitionRef.current = null;
			setListening(false);
		};
		recognitionRef.current = recognition;
		setListening(true);
		recognition.start();
	}, [lang]);
	useEffect(() => () => {
		recognitionRef.current?.abort();
		recognitionRef.current = null;
	}, []);
	return {
		listening,
		start,
		stop,
		supported
	};
};
//#endregion
export { useSpeechRecognition };

//# sourceMappingURL=useSpeechRecognition.js.map